import React, { useState } from 'react';
import { FiUpload, FiCheck, FiX, FiAlertCircle, FiClock, FiFile, FiEye, FiDownload, FiShield, FiRefreshCw } from 'react-icons/fi';
import { useProfile } from '../../context/ProfileContext';

const KYCModule = () => {
  const { kycDocuments, setKycDocuments, uploadDocument, verifyKycDocument, calculateProfileCompletion } = useProfile();
  const [uploadingDoc, setUploadingDoc] = useState(null);
  const [viewDoc, setViewDoc] = useState(null);
  const [verifying, setVerifying] = useState(null);

  const documentTypes = [
    {
      id: 'aadhar',
      name: 'Aadhar Card',
      icon: '🆔',
      required: true,
      fields: ['number', 'frontImage', 'backImage'],
      numberFormat: 'XXXX-XXXX-XXXX',
      numberLength: 12
    },
    {
      id: 'pan',
      name: 'PAN Card',
      icon: '💳',
      required: true,
      fields: ['number', 'image'],
      numberFormat: 'ABCDE1234F',
      numberLength: 10
    },
    {
      id: 'passport',
      name: 'Passport',
      icon: '📘',
      required: false,
      fields: ['number', 'frontImage', 'backImage', 'expiryDate'],
      numberFormat: 'A1234567',
      numberLength: 8
    },
    {
      id: 'drivingLicense',
      name: 'Driving License',
      icon: '🚗',
      required: false,
      fields: ['number', 'frontImage', 'backImage', 'expiryDate'],
      numberFormat: 'DL-1234567890',
      numberLength: 16
    },
    {
      id: 'voterCard',
      name: 'Voter ID Card',
      icon: '🗳️',
      required: false,
      fields: ['number', 'image'],
      numberFormat: 'ABC1234567',
      numberLength: 10
    },
    {
      id: 'businessRegistration',
      name: 'Business Registration',
      icon: '🏢',
      required: true,
      fields: ['documentType', 'documentNumber', 'documentImage'],
      types: ['Partnership Deed', 'MOA', 'AOA', 'Trust Deed', 'Society Registration']
    },
    {
      id: 'gstCertificate',
      name: 'GST Certificate',
      icon: '📋',
      required: true,
      fields: ['image']
    },
    {
      id: 'tradeLicense',
      name: 'Trade License',
      icon: '📄',
      required: false,
      fields: ['number', 'image', 'expiryDate']
    }
  ];

  const getVerificationStatus = (doc) => {
    if (doc.verified) return 'verified';
    if (doc.number || doc.image || doc.frontImage) return 'pending';
    return 'not-uploaded';
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'verified':
        return (
          <span className="badge bg-success bg-opacity-10 text-success rounded-pill px-3">
            <FiCheck className="me-1" /> Verified
          </span>
        );
      case 'pending':
        return (
          <span className="badge bg-warning bg-opacity-10 text-warning rounded-pill px-3">
            <FiClock className="me-1" /> Pending
          </span>
        );
      default:
        return (
          <span className="badge bg-secondary bg-opacity-10 text-secondary rounded-pill px-3">
            <FiX className="me-1" /> Not Uploaded
          </span>
        );
    }
  };

  const handleFileUpload = async (e, docId, field) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingDoc(`${docId}-${field}`);
    try {
      const uploadedFile = await uploadDocument('kyc', file, field);

      setKycDocuments(prev => ({
        ...prev,
        [docId]: {
          ...prev[docId],
          [field]: uploadedFile.url,
          uploadedAt: new Date().toISOString()
        }
      }));
    } catch (error) {
      console.error('Upload failed:', error);
    }
    setUploadingDoc(null);
  };

  const handleNumberChange = (docId, value) => {
    setKycDocuments(prev => ({
      ...prev,
      [docId]: {
        ...prev[docId],
        number: value
      }
    }));
  };

  const handleTypeChange = (docId, value) => {
    setKycDocuments(prev => ({
      ...prev,
      [docId]: {
        ...prev[docId],
        documentType: value
      }
    }));
  };

  const handleExpiryChange = (docId, value) => {
    setKycDocuments(prev => ({
      ...prev,
      [docId]: {
        ...prev[docId],
        expiryDate: value
      }
    }));
  };

  const verifyDocument = async (docId) => {
    const doc = kycDocuments[docId];
    if (!doc.number) {
      alert('Please enter the document number first');
      return;
    }

    setVerifying(docId);
    try {
      await verifyKycDocument(docId, doc.number);
      // Success message handled by context
    } catch (error) {
      console.error('Failed to verify document:', error);
      alert('Failed to verify document. Please try again.');
    } finally {
      setVerifying(null);
    }
  };

  const completion = calculateProfileCompletion();
  const requiredDocs = documentTypes.filter(d => d.required);
  const uploadedRequired = requiredDocs.filter(d => getVerificationStatus(kycDocuments[d.id]) !== 'not-uploaded').length;

  return (
    <div className="container-fluid p-0">
      {/* KYC Overview */}
      <div className="glass-card p-4 mb-4">
        <div className="row align-items-center">
          <div className="col-md-8">
            <h4 className="fw-bold mb-3">KYC Verification Status</h4>

            <div className="d-flex align-items-center gap-4 mb-3">
              <div>
                <small className="text-muted">Overall KYC Status</small>
                <h5 className="mb-0">
                  {kycDocuments.verificationStatus === 'verified' ? (
                    <span className="text-success"><FiCheck /> Fully Verified</span>
                  ) : kycDocuments.verificationStatus === 'partial' ? (
                    <span className="text-warning"><FiClock /> Partially Verified</span>
                  ) : (
                    <span className="text-danger"><FiAlertCircle /> Pending Verification</span>
                  )}
                </h5>
              </div>

              <div className="vr"></div>

              <div>
                <small className="text-muted">Required Documents</small>
                <h5 className="mb-0">{uploadedRequired} / {requiredDocs.length}</h5>
              </div>

              <div className="vr"></div>

              <div>
                <small className="text-muted">KYC Completion</small>
                <h5 className="mb-0">{completion.sections.kyc.toFixed(0)}%</h5>
              </div>
            </div>

            <div className="progress" style={{ height: '8px' }}>
              <div
                className="progress-bar bg-primary"
                role="progressbar"
                style={{ width: `${completion.sections.kyc}%` }}
              />
            </div>
          </div>

          <div className="col-md-4 text-end">
            <button className="btn btn-primary rounded-pill px-4">
              <FiRefreshCw className="me-2" />
              Re-verify All
            </button>
          </div>
        </div>
      </div>

      {/* Document Upload Grid */}
      <div className="row g-4">
        {documentTypes.map((docType) => {
          const doc = kycDocuments[docType.id];
          const status = getVerificationStatus(doc);

          return (
            <div key={docType.id} className="col-lg-6">
              <div className={`glass-card p-4 ${status === 'verified' ? 'border-success' : ''}`}>
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <div className="d-flex align-items-center gap-3">
                    <span className="fs-1">{docType.icon}</span>
                    <div>
                      <h5 className="mb-1 fw-bold">
                        {docType.name}
                        {docType.required && <span className="text-danger ms-1">*</span>}
                      </h5>
                      {getStatusBadge(status)}
                    </div>
                  </div>

                  {status === 'verified' && (
                    <FiShield className="text-success fs-3" />
                  )}
                </div>

                {/* Document Number Input */}
                {docType.fields.includes('number') && (
                  <div className="mb-3">
                    <label className="form-label small text-muted">
                      {docType.name} Number
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder={docType.numberFormat}
                      value={doc.number || ''}
                      onChange={(e) => handleNumberChange(docType.id, e.target.value)}
                      maxLength={docType.numberLength}
                    />
                  </div>
                )}

                {/* Document Type Selector for Business Registration */}
                {docType.id === 'businessRegistration' && (
                  <div className="mb-3">
                    <label className="form-label small text-muted">Document Type</label>
                    <select
                      className="form-select"
                      value={doc.documentType || ''}
                      onChange={(e) => handleTypeChange(docType.id, e.target.value)}
                    >
                      <option value="">Select Document Type</option>
                      {docType.types.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Document Number for Business Registration */}
                {docType.fields.includes('documentNumber') && (
                  <div className="mb-3">
                    <label className="form-label small text-muted">Document Number</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Enter document number"
                      value={doc.documentNumber || ''}
                      onChange={(e) => handleNumberChange(docType.id, e.target.value)}
                    />
                  </div>
                )}

                {/* Expiry Date */}
                {docType.fields.includes('expiryDate') && (
                  <div className="mb-3">
                    <label className="form-label small text-muted">Expiry Date</label>
                    <input
                      type="date"
                      className="form-control"
                      value={doc.expiryDate || ''}
                      onChange={(e) => handleExpiryChange(docType.id, e.target.value)}
                    />
                  </div>
                )}

                {/* File Upload Areas */}
                <div className="row g-2">
                  {/* Front Image */}
                  {docType.fields.includes('frontImage') && (
                    <div className="col-6">
                      <div className="upload-area position-relative">
                        {doc.frontImage ? (
                          <div className="uploaded-doc text-center p-3 border rounded">
                            <FiFile size={30} className="text-success mb-2" />
                            <p className="small mb-1">Front Side</p>
                            <div className="d-flex gap-2 justify-content-center">
                              <button
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => setViewDoc(doc.frontImage)}
                              >
                                <FiEye />
                              </button>
                              <button className="btn btn-sm btn-outline-secondary">
                                <FiDownload />
                              </button>
                            </div>
                          </div>
                        ) : (
                          <label className="upload-label d-block text-center p-3 border border-dashed rounded cursor-pointer">
                            {uploadingDoc === `${docType.id}-frontImage` ? (
                              <div className="spinner-border spinner-border-sm text-primary" />
                            ) : (
                              <>
                                <FiUpload size={24} className="text-muted mb-2" />
                                <p className="small mb-0 text-muted">Upload Front Side</p>
                              </>
                            )}
                            <input
                              type="file"
                              className="d-none"
                              accept="image/*"
                              onChange={(e) => handleFileUpload(e, docType.id, 'frontImage')}
                            />
                          </label>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Back Image */}
                  {docType.fields.includes('backImage') && (
                    <div className="col-6">
                      <div className="upload-area position-relative">
                        {doc.backImage ? (
                          <div className="uploaded-doc text-center p-3 border rounded">
                            <FiFile size={30} className="text-success mb-2" />
                            <p className="small mb-1">Back Side</p>
                            <div className="d-flex gap-2 justify-content-center">
                              <button
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => setViewDoc(doc.backImage)}
                              >
                                <FiEye />
                              </button>
                              <button className="btn btn-sm btn-outline-secondary">
                                <FiDownload />
                              </button>
                            </div>
                          </div>
                        ) : (
                          <label className="upload-label d-block text-center p-3 border border-dashed rounded cursor-pointer">
                            {uploadingDoc === `${docType.id}-backImage` ? (
                              <div className="spinner-border spinner-border-sm text-primary" />
                            ) : (
                              <>
                                <FiUpload size={24} className="text-muted mb-2" />
                                <p className="small mb-0 text-muted">Upload Back Side</p>
                              </>
                            )}
                            <input
                              type="file"
                              className="d-none"
                              accept="image/*"
                              onChange={(e) => handleFileUpload(e, docType.id, 'backImage')}
                            />
                          </label>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Single Image */}
                  {(docType.fields.includes('image') || docType.fields.includes('documentImage')) && (
                    <div className="col-12">
                      <div className="upload-area position-relative">
                        {doc.image || doc.documentImage ? (
                          <div className="uploaded-doc text-center p-3 border rounded">
                            <FiFile size={30} className="text-success mb-2" />
                            <p className="small mb-1">Document Uploaded</p>
                            <div className="d-flex gap-2 justify-content-center">
                              <button
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => setViewDoc(doc.image || doc.documentImage)}
                              >
                                <FiEye /> View
                              </button>
                              <button className="btn btn-sm btn-outline-secondary">
                                <FiDownload /> Download
                              </button>
                            </div>
                          </div>
                        ) : (
                          <label className="upload-label d-block text-center p-4 border border-dashed rounded cursor-pointer">
                            {uploadingDoc === `${docType.id}-${docType.fields.includes('image') ? 'image' : 'documentImage'}` ? (
                              <div className="spinner-border spinner-border-sm text-primary" />
                            ) : (
                              <>
                                <FiUpload size={30} className="text-muted mb-2" />
                                <p className="small mb-0 text-muted">
                                  Click to upload {docType.name}
                                </p>
                                <p className="text-muted small mb-0">
                                  JPG, PNG or PDF (Max 5MB)
                                </p>
                              </>
                            )}
                            <input
                              type="file"
                              className="d-none"
                              accept="image/*,.pdf"
                              onChange={(e) => handleFileUpload(e, docType.id, docType.fields.includes('image') ? 'image' : 'documentImage')}
                            />
                          </label>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Verification Actions */}
                {status === 'pending' && (
                  <div className="mt-3 d-flex justify-content-between align-items-center">
                    <small className="text-warning">
                      <FiAlertCircle className="me-1" />
                      Pending verification
                    </small>
                    <button
                      className="btn btn-sm btn-primary rounded-pill px-3"
                      onClick={() => verifyDocument(docType.id)}
                    >
                      Verify Now
                    </button>
                  </div>
                )}

                {status === 'verified' && doc.verificationDate && (
                  <div className="mt-3 text-success small">
                    <FiCheck className="me-1" />
                    Verified on {new Date(doc.verificationDate).toLocaleDateString()}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Document Viewer Modal */}
      {viewDoc && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: 'rgba(0,0,0,0.8)' }}
          onClick={() => setViewDoc(null)}
        >
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h5 className="modal-title">Document Preview</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setViewDoc(null)}
                />
              </div>
              <div className="modal-body text-center">
                <img
                  src={viewDoc}
                  alt="Document"
                  className="img-fluid rounded"
                  style={{ maxHeight: '500px' }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        .upload-label:hover {
          border-color: var(--bs-primary) !important;
          background: rgba(var(--bs-primary-rgb), 0.05);
        }
        .cursor-pointer {
          cursor: pointer;
        }
        .border-dashed {
          border-style: dashed !important;
        }
      `}</style>
    </div>
  );
};

export default KYCModule;